import React from 'react';
import { Box, Typography, Grid, Chip, Divider } from '@mui/material';
import { CalendarBlank, Users, MapPin, Heart } from '@phosphor-icons/react';
import useThemeStyles from '../../../../theme/useThemeStyles';

const OverviewSection = ({ weddingData }) => {
  const styles = useThemeStyles();

  const formatDate = (dateInput) => {
    if (!dateInput) return 'Not specified';
    try {
      const date = dateInput instanceof Date ? dateInput : new Date(dateInput);
      if (isNaN(date.getTime())) return 'Invalid date';
      return date.toLocaleDateString('en-US', {
        year: 'numeric',
        month: 'long',
        day: 'numeric'
      });
    } catch {
      return 'Invalid date';
    }
  };

  const formatTime = (timeInput) => {
    if (!timeInput) return 'Not specified';
    return timeInput;
  };

  const hasVenueData = (venue) => {
    if (!venue) return false;
    return !!(
      venue.name ||
      venue.address ||
      venue.date ||
      venue.time ||
      venue.capacity
    );
  };

  return (
    <Box>
      <Typography variant="h5" sx={{ fontWeight: 600, mb: 3, ...styles.text.primary }}>
        Wedding Overview
      </Typography>

      <Grid container spacing={3}>
        {/* Basic Information */}
        <Grid item xs={12} md={6}>
          <Box sx={{ mb: 3 }}>
            <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
              <CalendarBlank size={24} style={{ marginRight: 12, color: '#64748b' }} />
              <Typography variant="h6" sx={{ fontWeight: 600, ...styles.text.primary }}>
                Basic Information
              </Typography>
            </Box>
            <Box sx={{ pl: 4 }}>
              <Typography variant="body2" sx={{ mb: 1, ...styles.text.secondary }}>
                <strong>Wedding Date:</strong> {formatDate(
                  weddingData?.details?.venues?.ceremony?.date ||
                  weddingData?.wedding?.weddingDate
                )}
              </Typography>
              {weddingData?.wedding?.accessCode && (
                <Typography variant="body2" sx={{ ...styles.text.secondary }}>
                  <strong>Access Code:</strong> {weddingData.wedding.accessCode}
                </Typography>
              )}
            </Box>
          </Box>
        </Grid>

        {/* Quick Stats */}
        <Grid item xs={12} md={6}>
          <Box sx={{ mb: 3 }}>
            <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
              <Heart size={24} style={{ marginRight: 12, color: '#64748b' }} />
              <Typography variant="h6" sx={{ fontWeight: 600, ...styles.text.primary }}>
                Quick Stats
              </Typography>
            </Box>
            <Box sx={{ pl: 4, display: 'flex', flexDirection: 'column', gap: 1 }}>
              <Chip
                label={`${weddingData?.guests?.length || 0} Guests`}
                icon={<Users size={16} />}
                size="small"
                variant="outlined"
              />
              <Chip
                label={`${weddingData?.suppliers?.length || 0} Suppliers`}
                icon={<MapPin size={16} />}
                size="small"
                variant="outlined"
              />
              <Chip
                label={`${weddingData?.timeline?.length || 0} Timeline Events`}
                icon={<CalendarBlank size={16} />}
                size="small"
                variant="outlined"
              />
            </Box>
          </Box>
        </Grid>

        {/* Ceremony Venue */}
        {weddingData?.details?.venues?.ceremony && hasVenueData(weddingData.details.venues.ceremony) && (
          <Grid item xs={12}>
            <Divider sx={{ my: 2 }} />
            <Box>
              <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                <MapPin size={24} style={{ marginRight: 12, color: '#64748b' }} />
                <Typography variant="h6" sx={{ fontWeight: 600, ...styles.text.primary }}>
                  Ceremony Venue
                </Typography>
              </Box>
              <Box sx={{ pl: 4 }}>
                {weddingData.details.venues.ceremony.name && (
                  <Typography variant="body2" sx={{ mb: 1, ...styles.text.secondary }}>
                    <strong>Name:</strong> {weddingData.details.venues.ceremony.name}
                  </Typography>
                )}
                {weddingData.details.venues.ceremony.address && (
                  <Typography variant="body2" sx={{ mb: 1, ...styles.text.secondary }}>
                    <strong>Address:</strong> {weddingData.details.venues.ceremony.address}
                  </Typography>
                )}
                {weddingData.details.venues.ceremony.date && (
                  <Typography variant="body2" sx={{ mb: 1, ...styles.text.secondary }}>
                    <strong>Date:</strong> {formatDate(weddingData.details.venues.ceremony.date)}
                  </Typography>
                )}
                {weddingData.details.venues.ceremony.time && (
                  <Typography variant="body2" sx={{ mb: 1, ...styles.text.secondary }}>
                    <strong>Time:</strong> {formatTime(weddingData.details.venues.ceremony.time)}
                  </Typography>
                )}
                {weddingData.details.venues.ceremony.capacity && (
                  <Typography variant="body2" sx={{ ...styles.text.secondary }}>
                    <strong>Capacity:</strong> {weddingData.details.venues.ceremony.capacity}
                  </Typography>
                )}
              </Box>
            </Box>
          </Grid>
        )}

        {/* Reception Venue */}
        {weddingData?.details?.venues?.reception && 
         (hasVenueData(weddingData.details.venues.reception) || weddingData.details.venues.reception.sameAsCeremony) && (
          <Grid item xs={12}>
            <Divider sx={{ my: 2 }} />
            <Box>
              <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                <MapPin size={24} style={{ marginRight: 12, color: '#64748b' }} />
                <Typography variant="h6" sx={{ fontWeight: 600, ...styles.text.primary }}>
                  Reception Venue
                </Typography>
              </Box>
              <Box sx={{ pl: 4 }}>
                {weddingData.details.venues.reception.sameAsCeremony ? (
                  <Typography variant="body2" sx={{ ...styles.text.secondary }}>
                    Same as Ceremony Venue
                  </Typography>
                ) : (
                  <>
                    {weddingData.details.venues.reception.name && (
                      <Typography variant="body2" sx={{ mb: 1, ...styles.text.secondary }}>
                        <strong>Name:</strong> {weddingData.details.venues.reception.name}
                      </Typography>
                    )}
                    {weddingData.details.venues.reception.address && (
                      <Typography variant="body2" sx={{ mb: 1, ...styles.text.secondary }}>
                        <strong>Address:</strong> {weddingData.details.venues.reception.address}
                      </Typography>
                    )}
                    {weddingData.details.venues.reception.date && (
                      <Typography variant="body2" sx={{ mb: 1, ...styles.text.secondary }}>
                        <strong>Date:</strong> {formatDate(weddingData.details.venues.reception.date)}
                      </Typography>
                    )}
                    {weddingData.details.venues.reception.time && (
                      <Typography variant="body2" sx={{ mb: 1, ...styles.text.secondary }}>
                        <strong>Time:</strong> {formatTime(weddingData.details.venues.reception.time)}
                      </Typography>
                    )}
                    {weddingData.details.venues.reception.capacity && (
                      <Typography variant="body2" sx={{ ...styles.text.secondary }}>
                        <strong>Capacity:</strong> {weddingData.details.venues.reception.capacity}
                      </Typography>
                    )}
                  </>
                )}
              </Box>
            </Box>
          </Grid>
        )}
      </Grid>
    </Box>
  );
};

export default OverviewSection;

